'use client';

/**
 * Employer Settings Page
 *
 * Shows employer capabilities and settings including:
 * - Capability status (enabled/disabled features)
 * - Benefit programs gated by capabilities
 * - Account settings
 */

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Settings,
  Shield,
  CheckCircle,
  XCircle,
  Lock,
  Unlock,
  Gift,
  RefreshCw,
  AlertCircle,
  Info,
  Building2,
  Sparkles,
  Compass,
  Heart,
  Briefcase,
  BarChart3,
  MessageSquare,
  Users,
  Zap,
} from 'lucide-react';
import { getMemberData, getMemberToken } from '@/lib/memberAuth';
import { getWeaveinApiUrl } from '@/src/config';
import EmployerLayout from '../layout';
import {
  EMPLOYER_CAPABILITIES,
  EMPLOYER_CAPABILITY_BUNDLES,
  useEmployerCapabilities,
  useEmployerCapabilityTier,
  EmployerCapabilityName,
} from '@/hooks/useEmployerCapabilities';

interface Employer {
  id: number;
  company_name: string;
  employer_number: string;
  contract_status: string;
  cbo_id: number;
}

interface Employee {
  id: number;
  employer_id: number;
  first_name: string;
  last_name: string;
  job_title?: string;
  department?: string;
}

// Map capabilities to icons
const CAPABILITY_ICONS: Record<string, any> = {
  employer_platform: Shield,
  employer_employee_portal: Users,
  employer_navigation_sessions: Compass,
  employer_analytics: BarChart3,
  employer_recommendations: Sparkles,
  employer_nudge_engine: Zap,
  employer_hris_integration: Building2,
  employer_leave_disability: Briefcase,
  employer_workspace_messaging: MessageSquare,
  employer_protected_mode: Lock,
};

// Tier display names and colors
const TIER_CONFIG: Record<string, { label: string; color: string; bgColor: string; description: string }> = {
  none: {
    label: 'Not Active',
    color: 'text-gray-600',
    bgColor: 'bg-gray-100',
    description: 'No capabilities enabled',
  },
  basic: {
    label: 'Basic',
    color: 'text-blue-600',
    bgColor: 'bg-blue-100',
    description: 'Core platform and employee portal access',
  },
  standard: {
    label: 'Standard',
    color: 'text-green-600',
    bgColor: 'bg-green-100',
    description: 'Includes navigation sessions and analytics',
  },
  premium: {
    label: 'Premium',
    color: 'text-purple-600',
    bgColor: 'bg-purple-100',
    description: 'Full AI recommendations and HRIS integration',
  },
  enterprise: {
    label: 'Enterprise',
    color: 'text-indigo-600',
    bgColor: 'bg-indigo-100',
    description: 'Complete feature set with protected mode',
  },
};

export default function EmployerSettingsPage() {
  const [employer, setEmployer] = useState<Employer | null>(null);
  const [employee, setEmployee] = useState<Employee | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'capabilities' | 'account'>('capabilities');

  const member = getMemberData();

  // Fetch employer and employee data
  useEffect(() => {
    const fetchData = async () => {
      if (!member?.id) {
        console.log('No member ID found, skipping data fetch');
        setLoading(false);
        return;
      }

      try {
        const token = getMemberToken();
        if (!token) {
          console.warn('No member token - user not authenticated');
          setLoading(false);
          return;
        }

        // Fetch employee record
        const employeeUrl = getWeaveinApiUrl(`/api/employees/by-member/${member.id}`);
        console.log('Settings: Fetching employee data from:', employeeUrl);

        const employeeResponse = await fetch(employeeUrl, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'X-CBO-ID': localStorage.getItem('active_cbo_id') || '',
            'Content-Type': 'application/json',
          },
        });

        if (employeeResponse.ok) {
          const empData = await employeeResponse.json();
          console.log('Settings: Employee data loaded:', empData?.id);
          setEmployee(empData);

          // Fetch employer details
          if (empData.employer_id) {
            const employerUrl = getWeaveinApiUrl(`/api/employers/${empData.employer_id}`);
            const employerResponse = await fetch(employerUrl, {
              headers: {
                'Authorization': `Bearer ${token}`,
                'X-CBO-ID': localStorage.getItem('active_cbo_id') || '',
                'Content-Type': 'application/json',
              },
            });

            if (employerResponse.ok) {
              const empDataResult = await employerResponse.json();
              console.log('Settings: Employer data loaded:', empDataResult?.company_name);
              setEmployer(empDataResult);
            } else {
              console.warn('Settings: Failed to fetch employer:', employerResponse.status);
            }
          }
        } else {
          console.warn('Settings: Failed to fetch employee record:', employeeResponse.status);
        }
      } catch (error) {
        console.error('Error fetching settings data:', error);
        // Don't crash - continue with empty state
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [member?.id]);

  // Get capability data
  const allCapabilities = Object.keys(EMPLOYER_CAPABILITIES) as EmployerCapabilityName[];
  const { capabilities, loading: capsLoading, error: capsError } = useEmployerCapabilities(
    employer?.id || null,
    allCapabilities
  );
  const { tier, loading: tierLoading } = useEmployerCapabilityTier(employer?.id || null);

  const tierConfig = tier ? TIER_CONFIG[tier] : TIER_CONFIG.none;
  const enabledCount = Object.values(capabilities).filter(Boolean).length;
  const totalCount = Object.keys(EMPLOYER_CAPABILITIES).length;

  if (loading) {
    return (
      <EmployerLayout>
        <div className="flex items-center justify-center py-16">
          <RefreshCw className="w-8 h-8 text-indigo-600 animate-spin" />
          <span className="ml-3 text-gray-600">Loading settings...</span>
        </div>
      </EmployerLayout>
    );
  }

  return (
    <EmployerLayout>
      <div className="max-w-5xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Settings className="w-8 h-8 text-indigo-600" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
              <p className="text-gray-500">View your employer capabilities and account settings</p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8">
            <button
              onClick={() => setActiveTab('capabilities')}
              className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'capabilities'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Shield className="w-4 h-4 inline mr-2" />
              Capabilities
            </button>
            <button
              onClick={() => setActiveTab('account')}
              className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'account'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Building2 className="w-4 h-4 inline mr-2" />
              Account
            </button>
          </nav>
        </div>

        {/* Capabilities Tab */}
        {activeTab === 'capabilities' && (
          <div className="space-y-6">
            {/* Tier Summary Card */}
            <div className={`rounded-xl border-2 p-6 ${tierConfig.bgColor} border-opacity-50`}>
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center space-x-2">
                    <span className={`text-lg font-bold ${tierConfig.color}`}>
                      {tierConfig.label} Plan
                    </span>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${tierConfig.bgColor} ${tierConfig.color}`}>
                      {enabledCount}/{totalCount} Features
                    </span>
                  </div>
                  <p className="text-gray-600 mt-1">{tierConfig.description}</p>
                </div>
                {employer && (
                  <div className="text-right">
                    <p className="font-semibold text-gray-900">{employer.company_name}</p>
                    <p className="text-sm text-gray-500">Employer #{employer.employer_number}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Loading State */}
            {capsLoading && (
              <div className="flex items-center justify-center py-8">
                <RefreshCw className="w-6 h-6 text-indigo-600 animate-spin mr-2" />
                <span className="text-gray-600">Loading capabilities...</span>
              </div>
            )}

            {/* Error State */}
            {capsError && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-center">
                  <AlertCircle className="w-5 h-5 text-red-600 mr-2" />
                  <span className="text-red-800">{capsError}</span>
                </div>
              </div>
            )}

            {/* Capabilities Grid */}
            {!capsLoading && !capsError && (
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {allCapabilities.map((capName) => {
                  const capInfo = EMPLOYER_CAPABILITIES[capName];
                  const isEnabled = capabilities[capName];
                  const Icon = CAPABILITY_ICONS[capName] || Shield;

                  return (
                    <div
                      key={capName}
                      className={`rounded-lg border-2 p-4 transition-all ${
                        isEnabled
                          ? 'bg-white border-green-200 shadow-sm'
                          : 'bg-gray-50 border-gray-200'
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-center space-x-3">
                          <div className={`p-2 rounded-lg ${isEnabled ? 'bg-green-100' : 'bg-gray-200'}`}>
                            <Icon className={`w-5 h-5 ${isEnabled ? 'text-green-600' : 'text-gray-400'}`} />
                          </div>
                          <div>
                            <h3 className={`font-medium ${isEnabled ? 'text-gray-900' : 'text-gray-500'}`}>
                              {capInfo.label}
                            </h3>
                            <p className="text-xs text-gray-500 mt-0.5">{capInfo.description}</p>
                          </div>
                        </div>
                        {isEnabled ? (
                          <span className="flex items-center text-green-600">
                            <Unlock className="w-4 h-4" />
                          </span>
                        ) : (
                          <span className="flex items-center text-gray-400">
                            <Lock className="w-4 h-4" />
                          </span>
                        )}
                      </div>
                      <div className="mt-3 flex items-center justify-between">
                        <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                          capInfo.tier === 'basic' ? 'bg-blue-100 text-blue-700' :
                          capInfo.tier === 'standard' ? 'bg-green-100 text-green-700' :
                          capInfo.tier === 'premium' ? 'bg-purple-100 text-purple-700' :
                          'bg-indigo-100 text-indigo-700'
                        }`}>
                          {capInfo.tier.charAt(0).toUpperCase() + capInfo.tier.slice(1)}
                        </span>
                        {isEnabled ? (
                          <span className="text-xs text-green-600 font-medium flex items-center">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Active
                          </span>
                        ) : (
                          <span className="text-xs text-gray-400 flex items-center">
                            <XCircle className="w-3 h-3 mr-1" />
                            Not Available
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Info Box */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="text-sm font-medium text-blue-900">About Capabilities</h4>
                  <p className="text-sm text-blue-700 mt-1">
                    Capabilities determine which features and benefit programs are available to you.
                    Contact your HR department or administrator to upgrade your organization's plan.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Account Tab */}
        {activeTab === 'account' && (
          <div className="space-y-6">
            {/* Employer Info Card */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
              <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">Employer Information</h3>
              </div>
              <div className="p-6 space-y-4">
                {employer ? (
                  <>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium text-gray-500">Company Name</label>
                        <p className="mt-1 text-gray-900">{employer.company_name}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-500">Employer Number</label>
                        <p className="mt-1 text-gray-900">{employer.employer_number}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-500">Contract Status</label>
                        <p className="mt-1">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            employer.contract_status === 'ACTIVE'
                              ? 'bg-green-100 text-green-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {employer.contract_status || 'Active'}
                          </span>
                        </p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-500">Current Tier</label>
                        <p className="mt-1">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${tierConfig.bgColor} ${tierConfig.color}`}>
                            {tierConfig.label}
                          </span>
                        </p>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="text-center py-8">
                    <Building2 className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500">No employer information available</p>
                  </div>
                )}
              </div>
            </div>

            {/* Employee Info Card */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
              <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">Your Information</h3>
              </div>
              <div className="p-6 space-y-4">
                {employee ? (
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-gray-500">Name</label>
                      <p className="mt-1 text-gray-900">{employee.first_name} {employee.last_name}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Job Title</label>
                      <p className="mt-1 text-gray-900">{employee.job_title || 'Not specified'}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Department</label>
                      <p className="mt-1 text-gray-900">{employee.department || 'Not specified'}</p>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Users className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500">No employee information available</p>
                  </div>
                )}
              </div>
            </div>

            {/* Quick Links */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
              <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">Quick Links</h3>
              </div>
              <div className="p-6">
                <div className="grid sm:grid-cols-2 gap-3">
                  <Link
                    to="/employer/benefits"
                    className="flex items-center p-3 rounded-lg border border-gray-200 hover:border-indigo-300 hover:bg-indigo-50 transition-colors"
                  >
                    <Gift className="w-5 h-5 text-indigo-600 mr-3" />
                    <span className="font-medium text-gray-900">Browse Benefits</span>
                  </Link>
                  <Link
                    to="/employer/navigation"
                    className="flex items-center p-3 rounded-lg border border-gray-200 hover:border-indigo-300 hover:bg-indigo-50 transition-colors"
                  >
                    <Compass className="w-5 h-5 text-indigo-600 mr-3" />
                    <span className="font-medium text-gray-900">Get Navigation Help</span>
                  </Link>
                  <Link
                    to="/employer/wellness"
                    className="flex items-center p-3 rounded-lg border border-gray-200 hover:border-indigo-300 hover:bg-indigo-50 transition-colors"
                  >
                    <Heart className="w-5 h-5 text-indigo-600 mr-3" />
                    <span className="font-medium text-gray-900">Wellness Programs</span>
                  </Link>
                  <Link
                    to="/member/settings"
                    className="flex items-center p-3 rounded-lg border border-blue-200 hover:border-blue-400 hover:bg-blue-50 transition-colors"
                  >
                    <Shield className="w-5 h-5 text-blue-600 mr-3" />
                    <div>
                      <span className="font-medium text-gray-900">Health Plan Profile</span>
                      <p className="text-xs text-gray-500">Update insurance & coverage info</p>
                    </div>
                  </Link>
                  <Link
                    to="/member/dashboard"
                    className="flex items-center p-3 rounded-lg border border-gray-200 hover:border-indigo-300 hover:bg-indigo-50 transition-colors"
                  >
                    <Users className="w-5 h-5 text-indigo-600 mr-3" />
                    <span className="font-medium text-gray-900">Member Portal</span>
                  </Link>
                  <Link
                    to="/employer/leave"
                    className="flex items-center p-3 rounded-lg border border-gray-200 hover:border-indigo-300 hover:bg-indigo-50 transition-colors"
                  >
                    <Briefcase className="w-5 h-5 text-indigo-600 mr-3" />
                    <span className="font-medium text-gray-900">Leave & Disability</span>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </EmployerLayout>
  );
}
